/** Adapter between the API team member (raw Prisma + _count.clients) and the UI `TeamMember`. */

import type {
  AssignedClient,
  CompensationType,
  Currency,
  LinkedAccount,
  TeamMember,
  TeamRole,
  TeamStatus,
} from "@/types";
import { toTask, type ApiTask } from "@/features/tasks/tasks.adapter";
import type {
  AvailabilityStatus,
  CollaborationType,
  MemberDocType,
  SkillLevel,
  WorkMode,
} from "./team.constants";

export interface ApiMemberSkill {
  id: string;
  skillName: string;
  level: SkillLevel;
  category?: string | null;
  note?: string | null;
}

export interface ApiMemberDocument {
  id: string;
  documentType: MemberDocType;
  name?: string | null;
  fileId?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  status?: string | null;
  createdAt?: string | null;
}

export interface ApiMemberProjectLink {
  id: string;
  role?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  workloadPercentage?: number | string | null;
  status?: string | null;
  project: { id: string; name: string; status: string; clientId?: string | null };
}

export interface ApiMemberReport {
  id: string;
  name: string;
  role: TeamRole;
  status: string;
  position?: string | null;
}

export interface ApiTeamMember {
  id: string;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  photo?: string | null;
  address?: string | null;
  birthDate?: string | null;
  emergencyContact?: string | null;
  role: TeamRole;
  compensationType: CompensationType;
  salary?: number | string | null;
  currency?: Currency;
  status: TeamStatus;
  position?: string | null;
  departmentId?: string | null;
  managerId?: string | null;
  collaborationType?: CollaborationType | null;
  workMode?: WorkMode | null;
  joinDate?: string | null;
  trialPeriod?: boolean;
  trialEndDate?: string | null;
  availabilityStatus?: AvailabilityStatus | null;
  performanceScore?: number | string | null;
  avatarColor?: string | null;
  department?: { id: string; name: string } | null;
  manager?: { id: string; name: string; role: TeamRole } | null;
  skills?: ApiMemberSkill[] | null;
  reports?: ApiMemberReport[] | null;
  documents?: ApiMemberDocument[] | null;
  memberProjects?: ApiMemberProjectLink[] | null;
  goals?: unknown[] | null;
  userId?: string | null;
  linkedAccount?: LinkedAccount | null;
  user?: LinkedAccount | null;
  clients?: AssignedClient[] | null;
  assignedTasks?: ApiTask[] | null;
  _count?: { clients?: number; documents?: number; memberProjects?: number } | null;
  temporaryPassword?: string;
  accountCreated?: boolean;
  /** spec §4.2 — average of the 1-5 stars given by validators */
  avgRating?: number | null;
  ratingsCount?: number | null;
}

export interface SkillInput {
  skillName: string;
  level: SkillLevel;
  category?: string;
}

export interface TeamMemberInput {
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  photo?: string;
  address?: string;
  birthDate?: string;
  emergencyContact?: string;
  role: TeamRole;
  compensationType: CompensationType;
  salary: number;
  currency: Currency;
  status: TeamStatus;
  position?: string;
  departmentId?: string;
  managerId?: string;
  collaborationType?: CollaborationType;
  workMode?: WorkMode;
  joinDate?: string;
  trialPeriod?: boolean;
  trialEndDate?: string;
  availabilityStatus?: AvailabilityStatus;
  skills?: SkillInput[];
  createLoginAccount?: boolean;
  accountEmail?: string;
  temporaryPassword?: string;
  /** Nom du rôle du compte : un des 7 système OU un rôle personnalisé (texte libre). */
  accountRole?: string;
  /** Spec §3 — rôles multiples du compte (noms de rôles, au moins un, sans doublon). */
  accountRoles?: string[];
  accountIsActive?: boolean;
  mustChangePassword?: boolean;
  twoFactorEnabled?: boolean;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

export function toTeamMember(api: ApiTeamMember): TeamMember {
  const account = api.linkedAccount ?? api.user ?? null;
  const assignedClients = api.clients ?? undefined;
  const taskClientNames = new Map(
    (api.assignedTasks ?? []).flatMap((task): [string, string][] =>
      task.client ? [[task.client.id, task.client.name]] : [],
    ),
  );
  return {
    id: api.id,
    name: api.name,
    firstName: api.firstName ?? undefined,
    lastName: api.lastName ?? undefined,
    email: api.email,
    phone: api.phone ?? undefined,
    photo: api.photo ?? undefined,
    address: api.address ?? undefined,
    birthDate: api.birthDate ?? undefined,
    emergencyContact: api.emergencyContact ?? undefined,
    role: api.role,
    compensation: api.compensationType,
    salary: toNumber(api.salary),
    currency: api.currency ?? "DZD",
    status: api.status,
    position: api.position ?? undefined,
    departmentId: api.departmentId ?? api.department?.id ?? undefined,
    departmentName: api.department?.name ?? undefined,
    managerId: api.managerId ?? api.manager?.id ?? undefined,
    managerName: api.manager?.name ?? undefined,
    collaborationType: api.collaborationType ?? undefined,
    workMode: api.workMode ?? undefined,
    joinDate: api.joinDate ?? undefined,
    trialPeriod: api.trialPeriod ?? false,
    trialEndDate: api.trialEndDate ?? undefined,
    availabilityStatus: api.availabilityStatus ?? undefined,
    performanceScore:
      api.performanceScore !== null && api.performanceScore !== undefined
        ? toNumber(api.performanceScore)
        : undefined,
    avatarColor: api.avatarColor ?? undefined,
    userId: api.userId ?? account?.id ?? undefined,
    account,
    clientsCount: api._count?.clients ?? 0,
    documentsCount: api._count?.documents ?? api.documents?.length ?? 0,
    projectsCount: api._count?.memberProjects ?? api.memberProjects?.length ?? 0,
    assignedClients,
    skills: (api.skills ?? []).map((s) => ({
      id: s.id,
      skillName: s.skillName,
      level: s.level,
      category: s.category ?? undefined,
      note: s.note ?? undefined,
    })),
    documents: (api.documents ?? []).map((d) => ({
      id: d.id,
      documentType: d.documentType,
      name: d.name ?? undefined,
      fileId: d.fileId ?? undefined,
      issueDate: d.issueDate ?? undefined,
      expiryDate: d.expiryDate ?? undefined,
      status: d.status ?? undefined,
      createdAt: d.createdAt ?? undefined,
    })),
    memberProjects: (api.memberProjects ?? []).map((p) => ({
      id: p.id,
      role: p.role ?? undefined,
      startDate: p.startDate ?? undefined,
      endDate: p.endDate ?? undefined,
      workloadPercentage:
        p.workloadPercentage !== null && p.workloadPercentage !== undefined
          ? toNumber(p.workloadPercentage)
          : undefined,
      status: p.status ?? undefined,
      project: {
        id: p.project.id,
        name: p.project.name,
        status: p.project.status,
        clientId: p.project.clientId ?? undefined,
      },
    })),
    reports: (api.reports ?? []).map((r) => ({
      id: r.id,
      name: r.name,
      role: r.role,
      status: r.status,
      position: r.position ?? undefined,
    })),
    assignedTasks: api.assignedTasks?.map((task) => toTask(task, taskClientNames)),
    avgRating: api.avgRating ?? undefined,
    ratingsCount: api.ratingsCount ?? 0,
  };
}

export function toTeamWriteBody(input: TeamMemberInput): Record<string, unknown> {
  return {
    name: input.name,
    ...(input.firstName ? { firstName: input.firstName } : {}),
    ...(input.lastName ? { lastName: input.lastName } : {}),
    email: input.email,
    ...(input.phone ? { phone: input.phone } : {}),
    ...(input.photo ? { photo: input.photo } : {}),
    ...(input.address ? { address: input.address } : {}),
    ...(input.birthDate ? { birthDate: input.birthDate } : {}),
    ...(input.emergencyContact ? { emergencyContact: input.emergencyContact } : {}),
    role: input.role,
    compensationType: input.compensationType,
    salary: input.salary,
    currency: input.currency,
    status: input.status,
    ...(input.position ? { position: input.position } : {}),
    ...(input.departmentId ? { departmentId: input.departmentId } : {}),
    ...(input.managerId ? { managerId: input.managerId } : {}),
    ...(input.collaborationType ? { collaborationType: input.collaborationType } : {}),
    ...(input.workMode ? { workMode: input.workMode } : {}),
    ...(input.joinDate ? { joinDate: input.joinDate } : {}),
    ...(input.trialPeriod !== undefined ? { trialPeriod: input.trialPeriod } : {}),
    ...(input.trialEndDate ? { trialEndDate: input.trialEndDate } : {}),
    ...(input.availabilityStatus ? { availabilityStatus: input.availabilityStatus } : {}),
    ...(input.skills && input.skills.length > 0 ? { skills: input.skills } : {}),
    ...(input.createLoginAccount ? { createLoginAccount: true } : {}),
    ...(input.accountEmail ? { accountEmail: input.accountEmail } : {}),
    ...(input.temporaryPassword ? { temporaryPassword: input.temporaryPassword } : {}),
    ...(input.accountRoles?.length
      ? { accountRoles: input.accountRoles }
      : input.accountRole
        ? { accountRole: input.accountRole }
        : {}),
    ...(input.accountIsActive !== undefined ? { accountIsActive: input.accountIsActive } : {}),
    ...(input.mustChangePassword !== undefined
      ? { mustChangePassword: input.mustChangePassword }
      : {}),
    ...(input.twoFactorEnabled !== undefined ? { twoFactorEnabled: input.twoFactorEnabled } : {}),
  };
}

// ── Access panel (GET /team/:id/access) ──────────────────────────────────────

export interface AccessAccountRole {
  role: { id: string; name: string; label: string };
}

export interface AccessAccount {
  id: string;
  email: string;
  isActive: boolean;
  accountStatus: string;
  lastLoginAt?: string | null;
  lastActivityAt?: string | null;
  passwordChangedAt?: string | null;
  mustChangePassword?: boolean;
  twoFactorEnabled?: boolean;
  failedLoginAttempts?: number;
  invitationSentAt?: string | null;
  invitationExpiresAt?: string | null;
  createdAt?: string | null;
  userRoles?: AccessAccountRole[];
}

export interface AccessSession {
  id: string;
  device?: string | null;
  browser?: string | null;
  ipAddress?: string | null;
  loginAt?: string | null;
  logoutAt?: string | null;
  status?: string | null;
}

export interface AccessActivity {
  id: string;
  action: string;
  device?: string | null;
  browser?: string | null;
  ipAddress?: string | null;
  status?: string | null;
  createdAt?: string | null;
}

export interface MemberAccess {
  member: { id: string; name: string; email: string };
  account: AccessAccount | null;
  sessions: AccessSession[];
  activity: AccessActivity[];
}

export interface InvitationResult {
  ok: boolean;
  email: string;
  expiresAt?: string;
  activationToken: string;
}

/** The 6 access actions available on a member account. */
export type AccessAction = "suspend" | "activate" | "unblock" | "logout-all";

// ── Exceptions de permissions individuelles (spec RBAC v2 §3) ────────────────

export type PermissionOverrideMode = "GRANT" | "DENY";

export interface PermissionException {
  id: string;
  permissionKey: string;
  mode: PermissionOverrideMode;
  reason?: string | null;
  createdAt?: string | null;
  createdById?: string | null;
}

/** Corps du PUT — la liste envoyée REMPLACE l'existante. */
export interface PermissionExceptionInput {
  permissionKey: string;
  mode: PermissionOverrideMode;
  reason?: string;
}
